import { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Card, Grid, Button, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, useMediaQuery, useTheme,
} from '@mui/material';
import { PaymentOutlined } from '@mui/icons-material';
import axios from 'axios';
import { fmtMoney, locale } from '../utils/currency';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmtCur = (v) => fmtMoney(v);

export default function ParentFees() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [learners, setLearners] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [stmt, setStmt] = useState({ invoices: [], totalBilled: 0, totalPaid: 0, balance: 0 });
  const [loading, setLoading] = useState(true);
  const [paymentDlg, setPaymentDlg] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const lRes = await axios.get(`${API}/api/guardians/me/learners`, { headers: hdrs() });
        const kids = lRes.data?.data || [];
        setLearners(kids);
        if (kids.length > 0) {
          setSelectedLearner(kids[0]);
          loadFees(kids[0].admNo);
        }
      } catch (e) {
        console.error('Error loading learners:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const loadFees = async (admNo) => {
    try {
      const res = await axios.get(`${API}/api/finance/statements/${admNo}`, { headers: hdrs() });
      setStmt(res.data || { invoices: [], totalBilled: 0, totalPaid: 0, balance: 0 });
    } catch (e) {
      console.error('Error loading fees:', e);
    }
  };

  const fees = stmt.invoices || [];
  const totalDue = stmt.balance || 0;
  const totalPaid = stmt.totalPaid || 0;

  if (loading) return <Box textAlign="center" py={8}><CircularProgress /></Box>;

  return (
    <Container maxWidth="md" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box py={{ xs: 2, sm: 3 }}>
        <Typography variant="h5" fontWeight={700} mb={3} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>School Fees</Typography>

        {/* Learner Selector */}
        {learners.length > 1 && (
          <Box sx={{ mb: 3, display: 'flex', gap: 1, overflowX: 'auto', pb: 1, WebkitOverflowScrolling: 'touch' }}>
            {learners.map(l => (
              <Chip
                key={l._id}
                label={l.name}
                onClick={() => {
                  setSelectedLearner(l);
                  loadFees(l.admNo);
                }}
                color={selectedLearner?._id === l._id ? 'primary' : 'default'}
                variant={selectedLearner?._id === l._id ? 'filled' : 'outlined'}
                sx={{ flexShrink: 0 }}
              />
            ))}
          </Box>
        )}

        {/* Summary Cards */}
        <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card sx={{ textAlign: 'center', p: { xs: 1.25, sm: 2 }, height: '100%' }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={1} sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' }, fontWeight: 700 }}>
                TOTAL BILLED
              </Typography>
              <Typography fontWeight={700} sx={{ fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
                {fmtCur(stmt.totalBilled)}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ textAlign: 'center', p: { xs: 1.25, sm: 2 }, bgcolor: 'success.50', height: '100%' }}>
              <Typography variant="caption" color="success.main" display="block" mb={1} sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' }, fontWeight: 700 }}>
                PAID
              </Typography>
              <Typography fontWeight={700} color="success.main" sx={{ fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
                {fmtCur(totalPaid)}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ textAlign: 'center', p: { xs: 1.25, sm: 2 }, bgcolor: totalDue > 0 ? 'warning.50' : 'success.50', height: '100%' }}>
              <Typography variant="caption" color={totalDue > 0 ? 'warning.main' : 'success.main'} display="block" mb={1} sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' }, fontWeight: 700 }}>
                BALANCE
              </Typography>
              <Typography fontWeight={700} color={totalDue > 0 ? 'warning.main' : 'success.main'} sx={{ fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
                {fmtCur(totalDue)}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ textAlign: 'center', p: { xs: 1.25, sm: 2 }, height: '100%' }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={1} sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' }, fontWeight: 700 }}>
                INVOICES
              </Typography>
              <Typography fontWeight={700} sx={{ fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
                {fees.length || 0}
              </Typography>
            </Card>
          </Grid>
        </Grid>

        {/* Invoices — card list on mobile, table on desktop */}
        {fees.length === 0 ? (
          <Card sx={{ boxShadow: 1, borderRadius: 2, p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>No invoices yet</Typography>
          </Card>
        ) : (
          <>
            {/* Mobile: Card View */}
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 1.5 }}>
              {fees.map((f, i) => {
                const bal = (f.amount || 0) - (f.paid || 0);
                return (
                  <Card key={f._id || i} sx={{ p: 1.5, borderRadius: 1.5, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #eee' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                      <Box flex={1} minWidth={0}>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.9rem', color: '#333', wordBreak: 'break-word' }}>
                          {f.description}
                          {f.term ? ` (Term ${f.term}${f.academicYear ? ` ${f.academicYear}` : ''})` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {f.date ? new Date(f.date).toLocaleDateString(locale(), { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                        </Typography>
                      </Box>
                      <Chip
                        label={bal <= 0 ? 'Paid' : (f.paid || 0) > 0 ? 'Partial' : 'Pending'}
                        size="small"
                        color={bal <= 0 ? 'success' : 'warning'}
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 22, flexShrink: 0 }}
                      />
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', fontWeight: 600, display: 'block' }}>AMOUNT</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{fmtCur(f.amount)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', fontWeight: 600, display: 'block' }}>PAID</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'success.main' }}>{fmtCur(f.paid)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', fontWeight: 600, display: 'block' }}>BALANCE</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 700, color: bal > 0 ? 'warning.main' : 'text.primary' }}>{fmtCur(bal)}</Typography>
                      </Box>
                    </Box>
                  </Card>
                );
              })}
            </Box>

            {/* Desktop: Table View */}
            <Card sx={{ boxShadow: 1, borderRadius: 2, overflow: 'hidden', display: { xs: 'none', sm: 'block' } }}>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Paid</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Balance</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fees.map((f, i) => {
                      const bal = (f.amount || 0) - (f.paid || 0);
                      return (
                        <TableRow key={f._id || i} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{f.date ? new Date(f.date).toLocaleDateString(locale(), { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</TableCell>
                          <TableCell>
                            {f.description}
                            {f.term ? ` (Term ${f.term}${f.academicYear ? ` ${f.academicYear}` : ''})` : ''}
                          </TableCell>
                          <TableCell align="right">{fmtCur(f.amount)}</TableCell>
                          <TableCell align="right">{fmtCur(f.paid)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtCur(bal)}</TableCell>
                          <TableCell>
                            <Chip
                              label={bal <= 0 ? 'Paid' : (f.paid || 0) > 0 ? 'Partial' : 'Pending'}
                              size="small"
                              color={bal <= 0 ? 'success' : 'warning'}
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Card>
          </>
        )}

        {/* Payment Button */}
        {totalDue > 0 && (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<PaymentOutlined />}
              onClick={() => setPaymentDlg(true)}
            >
              Pay {fmtCur(totalDue)}
            </Button>
          </Box>
        )}

        {/* Payment Dialog */}
        <Dialog open={paymentDlg} onClose={() => setPaymentDlg(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
          <DialogTitle>Make Payment</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ py: 3 }}>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Learner: <strong>{selectedLearner?.name}</strong>
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Amount Due</Typography>
                <Typography variant="h5" fontWeight={700} color="primary">
                  {fmtCur(totalDue)}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Click the button below to proceed to payment portal
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPaymentDlg(false)}>Cancel</Button>
            <Button variant="contained" onClick={() => {
              window.location.href = `/payments?learner=${selectedLearner?.admNo}&amount=${totalDue}`;
            }}>
              Proceed to Pay
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}
